/**
 * Rolled Steel Channel Sections - Dimensions and Properties
 * Data source: "Rolled Steel Channels dimensions and properties.csv"
 * 
 * CHANNEL CROSS-SECTION GEOMETRY:
 * 
 *        ← b (Width of Flange) →
 *        ┌─────────────────────┐  ↑
 *        │  ← tf (Flange) →    │  │
 *        ├─────────────────────┤  │
 *        │                        │ h (Overall Depth)
 *        │  ← tw (Web) →          │
 *        │                        │
 *        ├─────────────────────┤  │
 *        │                        │
 *        └─────────────────────┘  ↓
 *        
 *        ↑← cyy (Centroid from back of web)
 *        
 * Key Dimensions:
 * - h: Overall Depth of Section
 * - b: Width of Flange  
 * - tf: Thickness of Flange
 * - tw: Thickness of Web
 * - r1: Radius at Root (internal curve)
 * - r2: Radius at Toe (external curve)
 * - D: Slope of Flange (degrees)
 * - cyy: Centre of Gravity (horizontal distance from back of web)
 */

export interface RolledSteelChannelData {
  designation: string;           // e.g., "ISJC 100"
  w: number;                      // Weight (kg/m)
  a: number;                      // Area (cm²)
  h: number;                      // Overall Depth (mm)
  b: number;                      // Width of Flange (mm)
  tf: number;                     // Thickness of Flange (mm)
  tw: number;                     // Thickness of Web (mm)
  cyy: number;                    // Centre of Gravity - Y-Y axis (cm)
  Ixx: number;                    // Moment of Inertia about X-X (cm⁴)
  Iyy: number;                    // Moment of Inertia about Y-Y (cm⁴)
  rxx: number;                    // Radius of Gyration about X-X (cm)
  ryy: number;                    // Radius of Gyration about Y-Y (cm)
  Zxx: number;                    // Section Modulus about X-X (cm³)
  Zyy: number;                    // Section Modulus about Y-Y (cm³)
  r1: number;                     // Radius at Root (mm)
  r2: number;                     // Radius at Toe (mm)
  D: number;                      // Slope of Flange (degrees)
  maxRivet: number;               // Maximum Size of Flange Rivet (mm)
  h1: number;                     // Connection Detail h1 (mm)
  h2: number;                     // Connection Detail h2 (mm)
  b1: number;                     // Connection Detail b1/2 (mm)
  C: number;                      // Connection Detail C (mm)
  g: number;                      // Connection Detail g (mm)
  g1Min: number;                  // Connection Detail g1 Min (mm)
}

/**
 * Complete dataset of Rolled Steel Channel sections from CSV
 * All dimensions and properties from standard rolled steel channel specifications
 */
export const rolledSteelChannelSections: RolledSteelChannelData[] = [
  {
    designation: "ISJC 100",
    w: 5.8,
    a: 7.41,
    h: 100,
    b: 45,
    tf: 5.1,
    tw: 3,
    cyy: 1.4,
    Ixx: 123.8,
    Iyy: 14.9,
    rxx: 4.09,
    ryy: 1.42,
    Zxx: 24.8,
    Zyy: 4.8,
    r1: 6,
    r2: 2,
    D: 91.5,
    maxRivet: 12,
    h1: 77,
    h2: 11.5,
    b1: 21,
    C: 4.5,
    g: 25,
    g1Min: 50
  },
  {
    designation: "ISJC 125",
    w: 7.9,
    a: 10.07,
    h: 125,
    b: 50,
    tf: 6.6,
    tw: 3,
    cyy: 1.64,
    Ixx: 270,
    Iyy: 25.7,
    rxx: 5.18,
    ryy: 1.6,
    Zxx: 43.2,
    Zyy: 7.6,
    r1: 6,
    r2: 2.5,
    D: 91.5,
    maxRivet: 16,
    h1: 98.9,
    h2: 13.1,
    b1: 23.5,
    C: 4.5,
    g: 28,
    g1Min: 50
  },
  {
    designation: "ISJC 150",
    w: 9.9,
    a: 12.65,
    h: 150,
    b: 55,
    tf: 6.9,
    tw: 3.6,
    cyy: 1.66,
    Ixx: 471.1,
    Iyy: 37.9,
    rxx: 6.1,
    ryy: 1.73,
    Zxx: 62.8,
    Zyy: 9.9,
    r1: 7,
    r2: 3,
    D: 91.5,
    maxRivet: 20,
    h1: 121.2,
    h2: 14.4,
    b1: 25.7,
    C: 5.1,
    g: 30,
    g1Min: 50
  }
];

/**
 * Get all available channel designations
 */
export const getRolledSteelChannelDesignations = (): string[] => {
  return rolledSteelChannelSections.map(section => section.designation);
};

/**
 * Get channel data by designation
 */
export const getRolledSteelChannelByDesignation = (designation: string): RolledSteelChannelData | null => {
  return rolledSteelChannelSections.find(section => section.designation === designation) || null;
};
